import { useCallback, useEffect, useRef, useState } from "react";
import {
  nextDelayMs,
  playheadAfter,
  shownCount,
  type ReplaySchedule,
  type ReplaySpeed,
} from "@/lib/replay-scheduler";

export interface ReplayPlayer {
  /** How many entries are on screen. */
  shown: number;
  playing: boolean;
  speed: ReplaySpeed;
  /** Every entry is shown. */
  ended: boolean;
  togglePlaying: () => void;
  changeSpeed: (speed: ReplaySpeed) => void;
  skipToEnd: () => void;
}

/**
 * Plays a replay schedule (sc-114198). Owns the playback state and the one pending timer; every timing
 * rule comes from `replay-scheduler`. The playhead is in recorded time and starts at 0, and playback
 * starts the moment a schedule arrives.
 *
 * Pause and a speed change first fold the wall time elapsed since the timer was armed into the playhead,
 * so resuming plays only the remainder of the interrupted wait and a new speed rescales only that
 * remainder. A late timer catches up: every entry due by then appears in the same render.
 */
export function useReplayPlayer(schedule: ReplaySchedule | null): ReplayPlayer {
  const [playheadMs, setPlayheadMs] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState<ReplaySpeed>(1);
  /** Wall-clock time at which the pending timer was armed from the current playhead. */
  const armedAtRef = useRef(0);

  const shown = schedule ? shownCount(schedule, playheadMs) : 0;
  const ended = schedule !== null && shown === schedule.steps.length;
  const active = playing && schedule !== null && !ended;

  useEffect(() => {
    if (!active || schedule === null) return;
    const delay = nextDelayMs(schedule, playheadMs, speed);
    if (delay === null) return;

    const armedAt = Date.now();
    armedAtRef.current = armedAt;
    const timer = window.setTimeout(() => {
      const nextAt = schedule.steps[shownCount(schedule, playheadMs)].atMs;
      const caughtUp = playheadAfter(schedule, playheadMs, Date.now() - armedAt, speed);
      setPlayheadMs(Math.max(nextAt, caughtUp));
    }, delay);

    return () => window.clearTimeout(timer);
  }, [active, schedule, playheadMs, speed]);

  /** The playhead with the wall time elapsed since the timer was armed folded in. */
  const foldedPlayhead = useCallback(
    () =>
      schedule === null
        ? playheadMs
        : playheadAfter(schedule, playheadMs, Date.now() - armedAtRef.current, speed),
    [schedule, playheadMs, speed]
  );

  const togglePlaying = useCallback(() => {
    if (active) {
      setPlayheadMs(foldedPlayhead());
      setPlaying(false);
    } else {
      setPlaying(true);
    }
  }, [active, foldedPlayhead]);

  const changeSpeed = useCallback(
    (next: ReplaySpeed) => {
      if (next === speed) return;
      if (active) setPlayheadMs(foldedPlayhead());
      setSpeed(next);
    },
    [active, speed, foldedPlayhead]
  );

  const skipToEnd = useCallback(() => {
    if (schedule === null) return;
    setPlayheadMs(schedule.totalMs);
    setPlaying(false);
  }, [schedule]);

  return { shown, playing: active, speed, ended, togglePlaying, changeSpeed, skipToEnd };
}
